// ─────────────────────────────────────────────────────────────────────────────
// authClient.js — sessions, as seen from the browser
//
// Installed once as global axios interceptors, so every call site in the app is
// covered without being touched.
//
// ── Requests ─────────────────────────────────────────────────────────────────
// Many components build `Authorization: Bearer <token>` once and keep it. The
// access token now lives 15 minutes and rotates, so a request interceptor
// swaps any Bearer header for the CURRENT stored token. Without it, every call
// after a refresh would first fail with the old token.
//
// ── 401: the session ─────────────────────────────────────────────────────────
//   code="token_expired"  → refresh, then retry the request once.
//   anything else         → the session is over. Clear it, go to /login.
//
// Refresh tokens are single use and the server treats a second use as theft:
// it revokes the whole session. Two TABS refreshing at the same moment would
// look exactly like that. So the refresh runs under a cross-tab lock
// (navigator.locks), and a tab that waited for the lock first checks whether
// another tab already refreshed — if so it just uses the new token.
//
// ── 403: what this session may do ────────────────────────────────────────────
//   code="reauth_required"     → ask the person to confirm it's them (passkey,
//                                or password for older accounts), then retry
//                                once. The dialog lives in ReauthDialog.jsx and
//                                registers itself with setReauthHandler().
//                                Background calls opt out with
//                                { skipReauth: true } in their axios config.
//   code="recovery_restricted" → announced as a window event
//                                ("fennira:restricted"); the dashboard banner
//                                explains the 24-hour limit.
//   code="passkey_required"    → a recovery session that has not created its
//                                new passkey yet: go to /recover.
// ─────────────────────────────────────────────────────────────────────────────

import axios from "axios";

const TOKEN_KEY   = "token";
const REFRESH_KEY = "refreshToken";
const USER_KEY    = "userId";

export const getToken        = () => safeGet(TOKEN_KEY);
export const getRefreshToken = () => safeGet(REFRESH_KEY);

function safeGet(key) {
  try { return window.localStorage.getItem(key) || ""; } catch { return ""; }
}
function safeSet(key, value) {
  try { if (value) window.localStorage.setItem(key, value); } catch { /* private mode */ }
}
function safeRemove(key) {
  try { window.localStorage.removeItem(key); } catch { /* private mode */ }
}

/** Stores whatever a sign-in / exchange / refresh response returned. */
export function storeSession(data) {
  if (!data) return;
  if (data.token)        safeSet(TOKEN_KEY, data.token);
  if (data.refreshToken) safeSet(REFRESH_KEY, data.refreshToken);
  if (data.userId)       safeSet(USER_KEY, String(data.userId));
}

export function clearSession() {
  safeRemove(TOKEN_KEY);
  safeRemove(REFRESH_KEY);
  safeRemove(USER_KEY);
}

export const hasSession = () => Boolean(getToken() || getRefreshToken());

/** Header object for a manual call. Kept so call sites can stay as they are. */
export const authHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Signs THIS device out on the server (the session and its refresh token),
 * then forgets the tokens locally. Never throws: signing out must always work.
 */
export async function logout() {
  const refreshToken = getRefreshToken();
  try {
    await axios.post("/api/logout", { refreshToken }, {
      headers: authHeader(), skipReauth: true, __noAuthRedirect: true
    });
  } catch { /* the local sign-out below still happens */ }
  clearSession();
}

// ── Refresh, shared across concurrent failures and across tabs ──────────────

let refreshInFlight = null;

function bearerOf(config) {
  const h = config?.headers?.Authorization || config?.headers?.authorization || "";
  return String(h).startsWith("Bearer ") ? String(h).slice(7) : "";
}

function doRefresh(failedToken) {
  const run = async () => {
    // Another tab may have refreshed while this one waited for the lock: the
    // stored token is then newer than the one that failed. Use it.
    const current = getToken();
    if (current && failedToken && current !== failedToken) return current;

    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error("no refresh token");

    // A bare axios call with __isRefresh, so a 401 here never re-enters the
    // refresh logic below.
    const res = await axios.post("/api/auth/refresh", { refreshToken }, {
      headers: { "Content-Type": "application/json" }, __isRefresh: true
    });
    if (!res.data?.token) throw new Error("refresh returned no token");
    storeSession(res.data);
    return res.data.token;
  };

  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    return navigator.locks.request("fennira-session-refresh", run);
  }
  return run();
}

function refreshSession(failedToken) {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh(failedToken).finally(() => { refreshInFlight = null; });
  }
  return refreshInFlight;
}

// ── Handlers the app registers ───────────────────────────────────────────────

let onSessionLost = null;
/** Lets the app decide how to leave (router navigate, state reset, …). */
export function setSessionLostHandler(handler) { onSessionLost = handler; }

let onPasskeyRequired = null;
export function setPasskeyRequiredHandler(handler) { onPasskeyRequired = handler; }

let reauthHandler = null;
/**
 * `handler(methods, { windowMinutes })` shows the "confirm it's you" dialog and
 * resolves true once the server has accepted a check, false if the person
 * cancelled. windowMinutes: how long the check will count (5 for account
 * security, a working day for daily work), so the dialog can say so.
 */
export function setReauthHandler(handler) { reauthHandler = handler; }

let reauthInFlight = null;
/** One dialog for any number of requests that need it at the same moment. */
export function requestReauth(methods = ["passkey"], info = {}) {
  if (!reauthHandler) return Promise.resolve(false);
  if (!reauthInFlight) {
    reauthInFlight = Promise.resolve(reauthHandler(methods, info))
      .catch(() => false)
      .finally(() => { reauthInFlight = null; });
  }
  return reauthInFlight;
}

function abandonSession() {
  clearSession();
  if (typeof onSessionLost === "function") {
    try { onSessionLost(); return; } catch { /* fall through to the reload */ }
  }
  try {
    if (!window.location.pathname.startsWith("/login")) window.location.assign("/login");
  } catch { /* non-browser environment */ }
}

/** True for an error that only means "the person cancelled the confirm dialog". */
export const isReauthCancelled = (err) => Boolean(err?.reauthCancelled);

/** Call once at startup. */
export function installAuthInterceptor() {
  axios.interceptors.request.use((config) => {
    const current = getToken();
    if (current && bearerOf(config) && !config.__isRefresh) {
      config.headers = { ...(config.headers || {}), Authorization: `Bearer ${current}` };
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const response = error.response;
      const config   = error.config || {};
      if (!response) return Promise.reject(error);
      const code = response.data?.code;

      // ── 403: allowed or not ──────────────────────────────────────────────
      if (response.status === 403) {
        if (code === "reauth_required" && !config.skipReauth && !config.__isReauthRetry) {
          const ok = await requestReauth(response.data?.methods, { windowMinutes: response.data?.windowMinutes });
          if (!ok) {
            error.reauthCancelled = true;
            return Promise.reject(error);
          }
          return axios({ ...config, __isReauthRetry: true });
        }
        if (code === "recovery_restricted") {
          try {
            window.dispatchEvent(new CustomEvent("fennira:restricted", { detail: { until: response.data?.until } }));
          } catch { /* old browser */ }
        }
        if (code === "passkey_required" && !config.__noAuthRedirect) {
          if (typeof onPasskeyRequired === "function") onPasskeyRequired();
          else if (!window.location.pathname.startsWith("/recover")) window.location.assign("/recover");
        }
        return Promise.reject(error);
      }

      // ── 401: the session ─────────────────────────────────────────────────
      if (response.status !== 401) return Promise.reject(error);
      if (config.__noAuthRedirect) return Promise.reject(error);

      // Never try to refresh the refresh endpoint, and never retry twice.
      if (config.__isRefresh || config.__isRefreshRetry || String(config.url || "").includes("/api/auth/refresh")) {
        abandonSession();
        return Promise.reject(error);
      }
      // Anything other than an expired access token is terminal.
      if (code && code !== "token_expired") {
        abandonSession();
        return Promise.reject(error);
      }
      if (!code && !getRefreshToken()) {
        abandonSession();
        return Promise.reject(error);
      }

      try {
        const freshToken = await refreshSession(bearerOf(config));
        const retry = { ...config, __isRefreshRetry: true };
        retry.headers = { ...(config.headers || {}), Authorization: `Bearer ${freshToken}` };
        return axios(retry);
      } catch {
        abandonSession();
        return Promise.reject(error);
      }
    }
  );
}
